import React from "react";
import { Typography, Box, Button, TextField, MenuItem } from "@mui/material";

import { Employee, UserMode } from "../../../../interfaces/User";
import { ServiceProvider } from "../../../../interfaces/ServiceProvidersInterface";
import EmployeeAndSpSelect from "../../../../components/Employee/EmployeeAndSpSelect";
import {
  convertADtoBS,
  getNepaliMonths
} from "../../../../components/Calendar/functions/calendarFunctions";
import { PayrollSettingsPanel } from "../../../ResourceCentre/ModuleSettings/PayrollSettings/PayrollSettingsPanel";
import useCurrentResourceCentre from "../../../../hooks/useCurrentResourceCentre";
import { PayrollAttendanceSource } from "../../../../constants/payroll";

interface SalaryListHeaderProps {
  enableCreateNew?: boolean;
  showEmployeeFilter?: boolean;
  showFilterOnly?: boolean;
  onRunPayroll?: () => void;
  onRunPyrollForAll?: () => void;
  selectedEmployee: Employee | ServiceProvider | null;
  setSelectedEmployee: (emp: Employee | ServiceProvider | null) => void;
  filter: {
    employeeIds: number[];
    serviceProviderIds: number[];
    payrollYear: number;
    payrollMonth: number;
  };
  setFilter: React.Dispatch<
    React.SetStateAction<{
      employeeIds: number[];
      serviceProviderIds: number[];
      payrollYear: number;
      payrollMonth: number;
    }>
  >;
}

// eslint-disable-next-line import/prefer-default-export
export const SalaryListHeader = ({
  enableCreateNew = true,
  showEmployeeFilter = true,
  showFilterOnly = false,
  onRunPayroll,
  onRunPyrollForAll,
  selectedEmployee,
  setSelectedEmployee,
  filter,
  setFilter
}: SalaryListHeaderProps): React.ReactNode => {
  const rc = useCurrentResourceCentre();

  const currentBs = convertADtoBS(new Date());
  const currentBsYear = currentBs.bsYear;
  const nepaliYears = Array.from({ length: 5 }, (_, i) => currentBsYear - i);
  const nepaliMonths = getNepaliMonths();

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
      {!showFilterOnly && (
        <Box
          sx={{
            width: "100%",
            px: 2,
            py: 1,
            display: "flex",
            gap: 1,
            flexDirection: { xs: "column", md: "row" },
            justifyContent: { xs: "center", md: "space-between" },
            alignItems: { xs: "flex-start", md: "center" }
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700
              }}
            >
              Payroll
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Run monthly payroll for the entire team, generate salaries on demand, and review past
              disbursals.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {enableCreateNew &&
              rc?.settings?.payrollSettings?.attendanceSource !==
                PayrollAttendanceSource.CUSTOM && (
                <Button
                  variant="outlined"
                  onClick={onRunPyrollForAll}
                  data-testmation="disburseSalaryBtn"
                >
                  Run Payroll for All
                </Button>
              )}

            {enableCreateNew && (
              <Button
                variant="contained"
                onClick={onRunPayroll}
                data-testmation="disburseSalaryBtn"
              >
                Run Employee Payroll
              </Button>
            )}

            <PayrollSettingsPanel />
          </Box>
        </Box>
      )}

      {/* filter */}
      <Box
        sx={{
          px: 2,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "center", md: "flex-end" },
          gap: 1,
          borderTop: 1,
          borderBottom: 1,
          borderColor: "divider"
        }}
      >
        {showEmployeeFilter && (
          <EmployeeAndSpSelect
            selectedEmployee={selectedEmployee}
            label="Select Employee"
            onChange={(emp) => {
              setSelectedEmployee(emp);
              setFilter((prev) => {
                if (!emp) {
                  return {
                    ...prev,
                    employeeIds: [],
                    serviceProviderIds: []
                  };
                }

                if (emp?.userAuthenticable[0]?.authenticable === UserMode.Employee) {
                  return {
                    ...prev,
                    employeeIds: emp ? [emp.id] : [],
                    serviceProviderIds: []
                  };
                }

                if (emp?.userAuthenticable[0]?.authenticable === UserMode.ServiceProvider) {
                  return {
                    ...prev,
                    employeeIds: [],
                    serviceProviderIds: emp ? [emp.userAuthenticable[0].id] : []
                  };
                }

                return prev;
              });
            }}
            sx={{ height: 35, width: 200 }}
          />
        )}

        <TextField
          select
          label="Payroll Year (BS)"
          value={filter.payrollYear}
          onChange={(e) => setFilter((prev) => ({ ...prev, payrollYear: Number(e.target.value) }))}
          variant="outlined"
          fullWidth
          size="small"
          sx={{ width: "150px" }}
          slotProps={{ inputLabel: { shrink: true } }}
        >
          {nepaliYears.map((year) => (
            <MenuItem key={year} value={year} disabled={year > currentBsYear}>
              {year}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Payroll Month (BS)"
          value={filter.payrollMonth}
          onChange={(e) => setFilter((prev) => ({ ...prev, payrollMonth: Number(e.target.value) }))}
          variant="outlined"
          fullWidth
          size="small"
          sx={{ width: "150px" }}
          slotProps={{ inputLabel: { shrink: true } }}
        >
          {nepaliMonths.map((mth) => (
            <MenuItem key={mth.month} value={mth.month}>
              {mth.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </Box>
  );
};
